/**
 * The localStorage half of the engine-availability check.
 *
 * The question — "are these engine ids deployed here" — changes about as often
 * as an environment is reprovisioned, so asking once per session per
 * environment is already more often than it needs to be. The cache makes it
 * less than once per session.
 *
 * WHAT INVALIDATES IT, and why each one is in the key rather than the value:
 *
 *   apiRoot    the environment. The same browser profile is used against
 *              local, stage and prod, and an answer from one says nothing
 *              about another.
 *   askedIds   the engines the app wanted to know about. Adding a language to
 *              the built-in table, or an org editing its `transcriptionEngines`
 *              map, changes the question — and an answer to a different
 *              question looks exactly like an answer to this one. Fingerprinted
 *              rather than stored so a changed set simply misses.
 *
 * TTL is the one thing the cache can be wrong about: an engine provisioned (or
 * removed) during the window is not noticed until it expires. A day is chosen
 * on that basis — long enough that it is one query per environment per working
 * day, short enough that a provisioning change lands the next morning without
 * anyone being told to clear site data. Shorten it, do not lengthen it: the
 * failure this guards against is a language MISSING from the dropdown, which
 * users report as the feature being broken.
 */

/** Bumped when the stored shape changes, so old entries miss rather than parse. */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `redact.engineAvailability.${CACHE_VERSION}`;

export const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedAnswer {
  checkedAt: number;
  availableIds: string[];
}

/**
 * Order-independent fingerprint of the ids asked about. Sorted because the set
 * is assembled by spreading maps, and object key order is not something to
 * make a cache key depend on.
 */
const fingerprint = (ids: readonly string[]): string => {
  const sorted = [...ids].sort();
  // djb2. A hash rather than the ids themselves, so the key does not grow with
  // the question: six UUIDs today (three languages x two engine types), but an
  // org configuring its own languages adds to that, and the ids are 36 chars
  // each. Sorting first is what makes it order-independent.
  let hash = 5381;
  for (const id of sorted) {
    for (let i = 0; i < id.length; i++) {
      hash = ((hash << 5) + hash + id.charCodeAt(i)) | 0;
    }
  }
  return (hash >>> 0).toString(36);
};

const keyFor = (apiRoot: string, askedIds: readonly string[]): string =>
  `${CACHE_PREFIX}.${apiRoot}.${fingerprint(askedIds)}`;

/**
 * Every access is wrapped: localStorage throws rather than returning null in a
 * private window and when site data is blocked, and a throw here must not take
 * out the audio tab. A miss and a failure are the same answer — ask the API.
 */
export const readCachedAvailability = (
  apiRoot: string | undefined,
  askedIds: readonly string[]
): readonly string[] | null => {
  if (!apiRoot) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(keyFor(apiRoot, askedIds));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedAnswer;
    if (
      !parsed ||
      !Array.isArray(parsed.availableIds) ||
      typeof parsed.checkedAt !== 'number' ||
      Date.now() - parsed.checkedAt > CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed.availableIds;
  } catch {
    return null;
  }
};

export const writeCachedAvailability = (
  apiRoot: string | undefined,
  askedIds: readonly string[],
  availableIds: readonly string[]
): void => {
  if (!apiRoot) {
    return;
  }
  try {
    const entry: CachedAnswer = {
      checkedAt: Date.now(),
      availableIds: [...availableIds],
    };
    window.localStorage.setItem(
      keyFor(apiRoot, askedIds),
      JSON.stringify(entry)
    );
  } catch {
    // Full, blocked, or private browsing. The check still works, it just runs
    // every session.
  }
};
